import { ChangeDetectionStrategy, Component, computed, input, model, signal } from '@angular/core'
import { FormValueControl } from '@angular/forms/signals'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'

/**
 * Ablagefläche für **eine oder mehrere** Dateien: hierher ziehen oder klicken.
 *
 * **`FormValueControl`**: der Wert liegt in einem `model()` und lässt sich damit an einen
 * Signal-Forms-Feldbaum binden (`[formField]="fg.dateien"`) oder ohne Formular verwenden
 * (`[(value)]="meinSignal"`).
 *
 * Bis 08/2026 war das ein Knopf „Datei auswählen" neben der Zeile „Keine Datei ausgewählt" — das las
 * sich wie ein Formularfeld und nicht wie ein Ort, an dem ein Beleg hingehört, und die gewählte Datei
 * war nur als Anzahl zu sehen („1 Datei ausgewählt"), nie mit Namen. Wer zwei Belege nacheinander
 * ablegte, konnte nicht prüfen, welcher jetzt drin ist. Selektor und Eingänge sind unverändert
 * geblieben, damit alle Aufrufer die Ablagefläche ohne Änderung bekommen.
 */
@Component({
  selector: 'lib-files-select',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './files-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './files-select.component.scss',
})
export class FilesSelectComponent implements FormValueControl<FileList | null> {
  readonly value = model<FileList | null>(null)

  readonly fileType = input('*')

  /** Beschriftung der leeren Fläche — sagt, WAS hier abgelegt werden soll. */
  readonly titel = input('Datei hier ablegen')

  protected readonly darueber = signal(false)

  protected readonly dateien = computed(() => Array.from(this.value() ?? []))

  protected ablegen(event: DragEvent) {
    event.preventDefault()
    this.darueber.set(false)
    const files = event.dataTransfer?.files
    if (files?.length) this.value.set(files)
  }

  protected dateienSetzen(files: FileList | null) {
    if (files?.length) this.value.set(files)
  }

  protected leeren() {
    this.value.set(null)
  }

  /** Dateigröße in der Einheit, die zur Zahl passt — „2,4 MB" liest sich schneller als „2411 kB". */
  protected groesse(datei: File): string {
    const kb = datei.size / 1024
    return kb < 1024 ? `${Math.max(1, Math.round(kb))} kB` : `${(kb / 1024).toFixed(1).replace('.', ',')} MB`
  }
}
